import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import "@/theme.css";
import App from "@/App";
import ErrorBoundary from "@/components/ErrorBoundary";
import "@/lib/i18n";  // bootstraps react-i18next before any component renders
import { installScriptErrorSilencer } from "@/lib/suppressScriptErrors";
import { hydrateBundledTranslations } from "@/lib/bundledTranslations";

installScriptErrorSilencer();
// Pre-seed the translation cache with the top ~20 most common UI
// strings translated into all 18 supported languages. On the very
// first language switch, every navigation tab, kicker, button
// label, and page title appears INSTANTLY — no waiting for the
// Claude round-trip. The hook handles the rest of the page text
// in the background.
hydrateBundledTranslations();

// Register the unified service worker for offline catalog + audio
// caching. Same file (`/push-sw.js`) the push-notifications flow
// already uses, so push subscribers don't get a second SW. Done in
// a `load` listener so the SW registration doesn't compete with
// the critical-path render. Errors are swallowed — SW is a pure
// enhancement; the app must still work without it (private mode,
// Brave with SW disabled, etc.).
//
// ── AUTO-UPDATE ──
// On every launch, compare the running bundle with the server's latest.
// If they differ, wipe service workers + caches and reload ONCE so the
// installed app always self-updates after a deploy (no ?sw=off needed).
(function autoUpdate() {
  try {
    fetch(`/index.html?upd=${Date.now()}`, { cache: "no-store" })
      .then((r) => r.text())
      .then(async (html) => {
        const m = html.match(/static\/js\/(main\.[a-f0-9]+\.js)/);
        if (!m) return;
        const latest = m[1];
        const current = Array.from(document.scripts).map((s) => s.src).join(" ");
        if (!current.includes("static/js/main.")) return; // dev server
        if (current.includes(latest)) return; // already newest
        if (sessionStorage.getItem("hw-reloaded-for") === latest) return; // loop guard
        sessionStorage.setItem("hw-reloaded-for", latest);
        await killAllServiceWorkers().catch(() => {});
        window.location.reload();
      })
      .catch(() => {});
  } catch (_) { /* never block the app */ }
})();

// ── ESCAPE HATCH ──
// If the user appends `?sw=off` to any URL OR has localStorage
// `hw-sw-disabled=1`, we tear down ANY existing service worker AND
// wipe every CacheStorage entry, then skip registration. This is a
// safety net for cases where a buggy older SW version is stuck
// serving stale content. Tell affected users to visit
// `https://holywords144.com/?sw=off` once and it self-heals.
async function killAllServiceWorkers() {
  try {
    if ("serviceWorker" in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
    if (typeof caches !== "undefined") {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
    // Persist the kill switch so accidental refreshes don't re-register.
    try { localStorage.setItem("hw-sw-disabled", "1"); } catch (_) {}
    // Reload once so the freshly-uncontrolled page makes a clean
    // network request for everything. Strip the ?sw=off so the URL
    // looks normal afterward.
    const u = new URL(window.location.href);
    u.searchParams.delete("sw");
    window.location.replace(u.toString());
  } catch (_) { /* best-effort */ }
}

const SW_KILL = (() => {
  try {
    const p = new URLSearchParams(window.location.search);
    if (p.get("sw") === "off") return true;
    if (p.get("sw") === "on") {
      // Manual re-enable: clear the persisted kill switch.
      try { localStorage.removeItem("hw-sw-disabled"); } catch (_) {}
      return false;
    }
    return localStorage.getItem("hw-sw-disabled") === "1";
  } catch (_) { return false; }
})();

if (SW_KILL && typeof window !== "undefined" && "serviceWorker" in navigator) {
  killAllServiceWorkers();
} else if (
  typeof window !== "undefined" &&
  "serviceWorker" in navigator &&
  window.location.protocol === "https:"
) {
  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/push-sw.js", { scope: "/", updateViaCache: "none" })
      .then((reg) => {
        // Force an immediate update check on every launch AND every time
        // the app returns to the foreground (installed PWAs can stay
        // "open" for days on iOS — without this they never re-check).
        try { reg.update(); } catch (_) {}
        document.addEventListener("visibilitychange", () => {
          if (document.visibilityState === "visible") {
            try { reg.update(); } catch (_) {}
          }
        });
      })
      .catch(() => {});
  });
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  </React.StrictMode>,
);
